import { useCallback, useEffect, useState } from "react"
import { Toast } from "./Toast"
import type { Position, Preset } from "./Toast"

export const GLOBAL_ID = "global"

interface ToastProps {
    message: string
    position?: Position
    preset?: Preset
    duration?: number
    showIcon?: boolean
    className?: string
    onClose?: () => void
}

interface ToastState extends ToastProps {
    timestamp: number
}

type ToastManagerApi = {
    fire: (props: ToastProps) => void
}

type ToastManagerProps = {
    /**
     * ID used to register the ToastManager.
     * Duplicate IDs will override the previous one.
     */
    id: string
}

const managerRegistry: Record<string, ToastManagerApi> = {}

/*
 * Add this component to control toasts from anywhere in the app.
 * This component registers itself and can be triggered using the
 * `fire` method, specifying which ToastManager ID to trigger or
 * "global" by default.
 *
 * @param {ToastManagerProps} the props to pass into this React component.
 *
 * @example
 * ```jsx
 * const ComponentWithToastManager = () => {
 *   return (
 *       <>
 *         <ToastManager id={"my-toast-id"} />
 *         Hello World
 *       </>
 *   )
 * }
 *
 * const ComponentToTriggerToast = () => {
 *   toast.fire({
 *     message: "hello toast",
 *     position: Position.top,
 *     preset: Preset.dark,
 *     showIcon: false,
 *     className: 'toastContained',
 *   }, "my-toast-id")
 * }
 * ```
 */
export const ToastManager = ({ id }: ToastManagerProps) => {
    const [toast, setToast] = useState<ToastState | null>(null)
    const toastManagerId = `$${id}` // Interpolation so the "user value" isn't directly used

    const handleClose = useCallback(() => {
        if (toast?.onClose) {
            toast.onClose()
        }
        setToast(null)
    }, [toast])

    const fire = useCallback((props: ToastProps) => {
        setToast({ ...props, timestamp: Date.now() })
    }, [])

    // Register/Unregister this ToastManager
    useEffect(() => {
        managerRegistry[toastManagerId] = { fire }
        return () => {
            delete managerRegistry[toastManagerId]
        }
    }, [toastManagerId, fire])

    return toast ? (
        <Toast key={toast.timestamp} {...toast} onClose={handleClose} />
    ) : null
}

export const toast = {
    fire: (props: ToastProps, target: string = GLOBAL_ID) => {
        const manager = managerRegistry[`$${target}`]
        if (!manager) {
            return
        }
        manager.fire(props)
    },
}
